import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import KEYS from '@rover/constants/src/platform/keys.constants';
import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Flex } from '@rover/kibble/core';

import { SelectFieldOptionConfig } from './SelectField.common';
import SelectFieldOption from './SelectFieldOption';

type SelectFieldOptionsProps = {
  id: string;
  options: SelectFieldOptionConfig[];
  selectedOption: SelectFieldOptionConfig | null;
  isOpen: boolean;
  onChange: (option: SelectFieldOptionConfig) => void;
  onDismiss: () => void;
  width?: string;
};

export interface SelectFieldOptionsHandle {
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleOptionFocus: (index?: number) => void;
}

const ALPHA_NUMERIC_ORDER: string[] = '0123456789abcdefghijklmnopqrstuvwxyz'.split('');
const REVERSE_ALPHA_NUMERIC_ORDER: string[] = ALPHA_NUMERIC_ORDER.slice().reverse();

const findNearestMatchingIndex = (
  options: SelectFieldOptionConfig[],
  startingLetter: string
): number => {
  let alphaNumIndex = ALPHA_NUMERIC_ORDER.indexOf(startingLetter);
  let matchingIndex = -1;

  // look "down" the alphabet for a matching option
  while (alphaNumIndex < ALPHA_NUMERIC_ORDER.length) {
    const char = ALPHA_NUMERIC_ORDER[alphaNumIndex];
    matchingIndex = options.findIndex((o) => o.label.toString().toLowerCase().startsWith(char));
    if (matchingIndex > -1) return matchingIndex; // if we found a match, return it
    alphaNumIndex += 1; // if we didn't find a match, try the next starting letter
  }

  // reinit and look "up" the alphabet for a matching option
  alphaNumIndex = REVERSE_ALPHA_NUMERIC_ORDER.indexOf(startingLetter);
  const reversedOptions = options.slice().reverse();
  while (alphaNumIndex < REVERSE_ALPHA_NUMERIC_ORDER.length) {
    const char = REVERSE_ALPHA_NUMERIC_ORDER[alphaNumIndex];
    matchingIndex = reversedOptions.findIndex((o) =>
      o.label.toString().toLowerCase().startsWith(char)
    );
    // if we found a match, return its original index
    if (matchingIndex > -1) return options.length - 1 - matchingIndex;
    alphaNumIndex += 1; // if we didn't find a match, try the next starting letter
  }

  // if all else fails, return the first index
  return 0;
};

const SelectFieldOptions = forwardRef<SelectFieldOptionsHandle, SelectFieldOptionsProps>(
  (
    {
      id,
      options,
      selectedOption,
      isOpen,
      onChange: handleChange,
      onDismiss: handleDismiss,
      width = '280px',
    },
    ref
  ) => {
    const optionRefs = useRef<Array<HTMLElement | null>>([]);
    const selectedIndex = options.findIndex((option) => option.value === selectedOption?.value);
    const initialIndex = selectedIndex > -1 ? selectedIndex : 0;
    const [focusedIndex, setFocusedIndex] = useState<number | undefined>(undefined);

    const handleOptionFocus = useCallback(
      (index: number | undefined): void => {
        if (typeof index === 'undefined') {
          setFocusedIndex(initialIndex);
          setTimeout(() => optionRefs.current[initialIndex]?.focus());
          return;
        }

        setFocusedIndex(index);
        setTimeout(() => optionRefs.current[index]?.focus());
      },
      [optionRefs, initialIndex]
    );

    useEffect(() => {
      if (!isOpen) setFocusedIndex(undefined);
    }, [isOpen]);

    const focusNextMatchingOption = useCallback(
      (event: React.KeyboardEvent): void => {
        const key = event.key.toLowerCase();
        const isFocused = typeof focusedIndex !== 'undefined';

        const matchingIndex = findNearestMatchingIndex(options, key);

        // Prevent the default behavior only if an option matches the key.
        event.preventDefault();
        event.stopPropagation();

        // If no option has focus or the focused option is not the match, focus the matching option.
        if (!isFocused || matchingIndex !== focusedIndex) {
          handleOptionFocus(matchingIndex);
          return;
        }

        // If the focused option is the match, focus the next matching option.
        const nextMatchingIndex = options.findIndex(
          (option, index) => option.label.toLowerCase().startsWith(key) && index > focusedIndex
        );

        // If there is no next matching option, focus the first matching option.
        const indexToFocus = nextMatchingIndex > -1 ? nextMatchingIndex : matchingIndex;
        handleOptionFocus(indexToFocus);
      },
      [focusedIndex, handleOptionFocus, options]
    );

    const handleKeyDown = useCallback<React.KeyboardEventHandler>(
      (event): void => {
        const isFocused = typeof focusedIndex !== 'undefined';
        const prevIndex = isFocused ? focusedIndex - 1 : undefined;
        const nextIndex = isFocused ? focusedIndex + 1 : undefined;

        switch (event.key) {
          case KEYS.ESCAPE:
            event.preventDefault();
            setFocusedIndex(undefined);
            handleDismiss();
            break;

          case KEYS.UP:
          case KEYS.LEFT:
            event.preventDefault();
            if (!isFocused) handleOptionFocus(options.length - 1);
            else if ((prevIndex as number) >= 0) handleOptionFocus(prevIndex);
            break;

          case KEYS.DOWN:
          case KEYS.RIGHT:
            event.preventDefault();
            if (!isFocused) handleOptionFocus(0);
            else if ((nextIndex as number) < options.length) handleOptionFocus(nextIndex);
            break;

          case KEYS.HOME:
            event.preventDefault();
            handleOptionFocus(0);
            break;

          case KEYS.END:
            event.preventDefault();
            handleOptionFocus(options.length - 1);
            break;

          case KEYS.ENTER:
          case KEYS.TAB:
          case KEYS.SPACE:
            event.preventDefault();
            if (isFocused) {
              handleChange(options[focusedIndex]);
            } else {
              handleDismiss();
            }
            break;

          default:
            focusNextMatchingOption(event);
            break;
        }
      },
      [
        focusedIndex,
        focusNextMatchingOption,
        handleChange,
        handleOptionFocus,
        handleDismiss,
        options,
      ]
    );

    useImperativeHandle(ref, () => ({
      handleKeyDown,
      handleOptionFocus,
    }));

    return (
      <Flex
        id={id}
        display={isOpen ? 'block' : 'none'}
        flexDirection="column"
        width={width}
        variant="elevation_mid"
        position="absolute"
        maxHeight="300px"
        borderRadius="4px"
        sx={{
          overflow: 'hidden',
          overflowY: 'auto',
          cursor: 'pointer',
          '& > :first-child': { borderTopLeftRadius: '4px', borderTopRightRadius: '4px' },
          '& > :last-child': { borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' },
          zIndex: ZIndex.DROPDOWN.toNumber(),
        }}
        webProps={{
          onKeyDown: handleKeyDown,
          role: 'listbox',
        }}
      >
        {options.map((option, index) => {
          const isSelected = selectedOption?.value === option.value;
          return (
            <SelectFieldOption
              id={`option-${id}-${option.value}`}
              key={option.value}
              option={option}
              ref={(el) => (optionRefs.current[index] = el)}
              isSelected={isSelected}
              onClick={handleChange}
            />
          );
        })}
      </Flex>
    );
  }
);

export default SelectFieldOptions;
